import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { billing } from '@/api/billing';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Loader2, TrendingUp, CheckCircle2, AlertTriangle, Users,
} from 'lucide-react';
import { cn } from '@/lib/cn';

function formatMrr(cents: number) {
  if (cents >= 100_000) return `$${(cents / 100_000).toFixed(1)}k`;
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
}

// Status labels are resolved via t() inside the component to support runtime language switching.
const STATUS_STYLE_CLASSES: Record<string, string> = {
  active:     'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900',
  past_due:   'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900',
  canceled:   'bg-muted text-muted-foreground border-border',
  trialing:   'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900',
  incomplete: 'bg-muted text-muted-foreground border-border',
  none:       'bg-muted text-muted-foreground border-border',
};

interface KpiCardProps { label: string; value: string | number; icon: React.ElementType; className?: string }
function KpiCard({ label, value, icon: Icon, className }: KpiCardProps) {
  return (
    <Card className={cn('', className)}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">{label}</p>
            <p className="text-2xl font-bold tabular-nums">{value}</p>
          </div>
          <Icon className="h-5 w-5 text-muted-foreground/40 mt-1 shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
}

function BillingOverviewInner() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function statusLabel(status: string): string {
    const map: Record<string, string> = {
      active: t('billing.statusActive'),
      past_due: t('billing.statusPastDue'),
      canceled: t('billing.statusCanceled'),
      trialing: t('billing.statusTrial'),
      incomplete: t('billing.statusPending'),
      none: t('billing.statusNoPlan'),
    };
    return map[status] ?? status;
  }

  const { data, isLoading } = useQuery({
    queryKey: ['admin-billing-overview'],
    queryFn: () => billing.getAdminOverview(),
  });

  return (
    <>
      <PageHeader
        eyebrow={t('navGroups.settings')}
        title={t('billing.overviewTitle')}
        description={t('billing.overviewDescription')}
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* KPI row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard
              label={t('billing.monthlyMrr')}
              value={data?.kpis ? formatMrr(data.kpis.total_mrr_cents) : '—'}
              icon={TrendingUp}
            />
            <KpiCard
              label={t('billing.active')}
              value={data?.kpis.active_count ?? 0}
              icon={CheckCircle2}
              className="border-emerald-200/60 dark:border-emerald-900/60"
            />
            <KpiCard
              label={t('billing.pastDue')}
              value={data?.kpis.past_due_count ?? 0}
              icon={AlertTriangle}
              className={data?.kpis.past_due_count ? 'border-amber-200/60 dark:border-amber-900/60' : ''}
            />
            <KpiCard
              label={t('billing.noPlan')}
              value={data?.kpis.none_count ?? 0}
              icon={Users}
            />
          </div>

          {/* Reseller table */}
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr>
                  {[t('billing.columnReseller'), t('billing.columnPlan'), t('billing.columnStatus'), t('billing.columnMonthlyValue'), t('billing.columnStripeCustomer')].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data?.rows ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground text-sm">
                      {t('billing.noResellersYet')}
                    </td>
                  </tr>
                ) : (data?.rows ?? []).map((row) => {
                  const statusClass = STATUS_STYLE_CLASSES[row.billing_status] ?? STATUS_STYLE_CLASSES.none!;
                  return (
                    <tr
                      key={row.reseller_id}
                      className="border-b border-border/50 hover:bg-muted/20 transition-colors cursor-pointer"
                      onClick={() => navigate(`/resellers/${row.reseller_id}`)}
                    >
                      <td className="px-4 py-3 font-medium">{row.reseller_name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.plan_name ?? <span className="italic">none</span>}</td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-block rounded-full border px-2 py-0.5 text-xs font-semibold', statusClass)}>
                          {statusLabel(row.billing_status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 tabular-nums">
                        {row.monthly_price_cents != null
                          ? `$${(row.monthly_price_cents / 100).toFixed(0)}/mo`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {row.stripe_customer_id
                          ? <span>{row.stripe_customer_id.slice(0, 14)}…</span>
                          : <span className="italic">{t('billing.notSet')}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data?.kpis.canceled_count ? (
            <p className="text-xs text-muted-foreground">
              {t(data.kpis.canceled_count > 1 ? 'billing.canceledNote_plural' : 'billing.canceledNote', { count: data.kpis.canceled_count })}
            </p>
          ) : null}
        </div>
      )}
    </>
  );
}

export function BillingOverview() {
  const user = useCurrentUser();
  if (!user.is_super_admin) return <Navigate to="/dashboard" replace />;
  return <BillingOverviewInner />;
}
